import { Form, Spin } from "antd";
import type { FormInstance } from "antd/es/form";
import _ from "lodash";
import React, {
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useState,
} from "react";
import { generateFormFields } from "utils/formUtils";

import AppLoader from "./AppLoader";
import BaseModal, { BaseModalProps, BaseModalRef } from "./BaseModal";

interface BaseModalFormProps extends BaseModalProps {
  onOk: (fields: Record<string, any>) => Promise<any>;
  parseError?: (error: any) => any;
  loading?: boolean;
  children?: ReactNode;
  initialValues?: Record<string, any>;
  onValuesChange?: (
    changedValues: Record<string, any>,
    allValues: Record<string, any>,
  ) => void;
  form?: FormInstance<any>;
  fields?: any[];
}

const BaseModalForm = forwardRef(
  (
    {
      afterClose,
      onOk,
      parseError,
      loading = false,
      modalProps,
      children,
      ...props
    }: BaseModalFormProps,
    ref: Ref<BaseModalRef>,
  ) => {
    const [response, setResponse] = useState<any>();
    const [form] = Form.useForm(props.form);

    const _afterClose = useCallback(() => {
      afterClose && afterClose(response);
    }, [response, afterClose]);

    if (modalProps?.onOk) {
      console.error("BaseModalForm - onOk should be outside modalProps.");
    }

    const _onOk = useCallback(async () => {
      await form.validateFields();
      try {
        const _response = await onOk(form.getFieldsValue());
        setResponse(_response || true);
      } catch (error) {
        form.setFields(parseError ? parseError(error) : []);
        throw error;
      }
    }, [onOk, parseError, form]);

    const _modalProps = {
      ...modalProps,
      title: props.title,
      onOk: _onOk,
    };

    return (
      <BaseModal
        {...props}
        modalProps={_modalProps}
        ref={ref}
        afterClose={_afterClose}
      >
        <Spin spinning={loading} indicator={<AppLoader />}>
          <Form
            layout="vertical"
            form={form}
            initialValues={props.initialValues}
            onValuesChange={props.onValuesChange}
          >
            {generateFormFields(props.fields)}
            {children}
          </Form>
        </Spin>
      </BaseModal>
    );
  },
);

export default BaseModalForm;
